/**
 * The ONE session-refresh step, for any axios transport (POS-SESSION-STATION-TIMERS-001 U7c; THINKPAD's ruling, "One
 * `retryAfterSessionRefresh` called by both transports").
 *
 * A request this transport sent with the stored session token, answered 401, means the token went stale: the step
 * refreshes the session ONCE and re-sends the request under the new token. It was `createApiClient`'s own inline block,
 * so a transport built outside the factory (pos-web's `ApiClientManager`) could not take it, and never refreshed at all:
 * a register whose access token expired had its calls refused with no refresh tried. It is now a step beside
 * `retryAfterStepUp` and `retryWhileIdempotencyInProgress`, and every authenticated transport calls it
 * (`sessionRefreshByConstruction.contract`).
 *
 * The rules, unchanged from the block they were:
 *
 * - **NO-SESSION-401:** only a request the transport stamped `sessionToken` refreshes. A 401 on a request that carried
 *   no session, or the caller's own credential, is that request's refusal: it never refreshes and never ends a session.
 * - **Once:** `_retryAfterRefresh` marks a request already re-sent.
 * - **Abort:** a request aborted before the refresh spends none; one aborted during it is not re-sent.
 * - **D-1 / D2-Q1:** a BACKGROUND request never keeps the session alive on its own. With nobody at the page since the
 *   token was first used, the token is paused for background requests, nothing is refreshed, and the session is not
 *   ended (`BackgroundRequestPausedError`).
 * - **U15 (owner ruling Q6, "Keep session, all apps"):** a refresh the server could not ANSWER is not a refusal. The
 *   session is kept (`onRefreshFailed` is not told) and the transport's own `unreachable` handling rejects the request.
 *   Any other refresh failure is a defect and passes through unchanged.
 * - A refusal the server served ends the session through `onRefreshFailed` (when the transport has one), then rejects
 *   through the transport's own `reject`.
 *
 * @module @puffin-core/api-client/sessionRefreshRetry
 */

import { isSessionUnreachable } from './errors/SessionUnreachableError.js';
import {
  BackgroundRequestPausedError,
  BACKGROUND_PAUSE_REASONS,
} from './errors/BackgroundRequestPausedError.js';

/** @returns {DOMException} the standard abort, so a caller filters it as it filters a native fetch abort */
function aborted() {
  return new DOMException('The operation was aborted.', 'AbortError');
}

/**
 * @param {Object} params
 * @param {Function} params.client - the axios instance (callable with a request config)
 * @param {*} params.error - the rejection being handled
 * @param {?() => Promise<?string>} params.onTokenRefresh - the transport's refresh: the new token, or null when the
 *   server refused it; a transport without one never refreshes
 * @param {?(detail: {reason: string}) => void} [params.onRefreshFailed] - ends the session after a served refusal
 * @param {{mayRefreshInBackground: Function, pauseBackground: Function}} params.sessionMonitor - who is at the page
 * @param {(error: *) => Promise<never>} params.reject - the transport's rejection of a refusal
 * @param {(failure: Error) => Promise<never>} params.unreachable - the transport's rejection when the refresh could not
 *   be answered (U15)
 * @returns {?Promise<*>} the re-sent request's settlement, or null when this is no refresh to take
 */
export function retryAfterSessionRefresh({
  client,
  error,
  onTokenRefresh,
  onRefreshFailed,
  sessionMonitor,
  reject,
  unreachable,
}) {
  const config = error?.config;
  if (
    error?.response?.status !== 401 ||
    !config ||
    !config.sessionToken ||
    config._retryAfterRefresh ||
    !onTokenRefresh
  ) {
    return null;
  }
  return refreshAndResend({
    client,
    error,
    config,
    onTokenRefresh,
    onRefreshFailed,
    sessionMonitor,
    reject,
    unreachable,
  });
}

/**
 * The refresh and the re-send, once `retryAfterSessionRefresh` has judged the step applies.
 *
 * @param {Object} params - `retryAfterSessionRefresh`'s, with the request's `config`.
 * @returns {Promise<*>}
 */
async function refreshAndResend({
  client,
  error,
  config,
  onTokenRefresh,
  onRefreshFailed,
  sessionMonitor,
  reject,
  unreachable,
}) {
  if (config.signal?.aborted) throw aborted();

  if (config.background === true && !sessionMonitor.mayRefreshInBackground(config.sessionToken)) {
    sessionMonitor.pauseBackground(config.sessionToken);
    throw new BackgroundRequestPausedError({
      endpoint: config.url,
      method: config.method,
      reason: BACKGROUND_PAUSE_REASONS.IDLE,
    });
  }

  config._retryAfterRefresh = true;

  let newToken;
  try {
    newToken = await onTokenRefresh();
  } catch (refreshFailure) {
    if (!isSessionUnreachable(refreshFailure)) throw refreshFailure;
    if (config.signal?.aborted) throw aborted();
    return unreachable(refreshFailure);
  }

  if (config.signal?.aborted) throw aborted();

  if (newToken) {
    config.headers.Authorization = `Bearer ${newToken}`;
    // The re-send carries the transport's own (new) session token, so its header is recognised as such.
    config.sessionToken = newToken;
    return client(config);
  }

  if (onRefreshFailed) onRefreshFailed({ reason: '401_refresh_failed' });
  return reject(error);
}
